import { getNamespacedResource } from "SharedStores/util";
import { deepClone, numericCompare } from "Util/util";
import { SIGNATORY_TAG_TYPES, TEMPLATE_UPGRADE_STATUS, canTagBeMapped } from "TemplateTags/templateTagTypes";

const DOCUMENT_TEMPLATE_NAMESPACE = "documentTemplate";

// getters
// Getters that return a function are prefixed with "get". Getters that return
// an object are not.
const TEMPLATE_DEFINITION_LIST = "templateDefinitionList";
const SMART_TEMPLATE_DEFINITION_LIST = "smartTemplateDefinitionList";
const GET_TEMPLATE_DEFINITION_FROM_ID = "getTemplateDefinitionFromId";
const GET_CURRENT_TEMPLATE_FROM_DEFINITION_ID = "getCurrentTemplateFromDefinitionId";
const GET_ORDERED_MERGE_TEMPLATE_MAPPINGS_FROM_DEFINITION_ID = "getOrderedMergeTemplateMappingsFromDefinitionId";
const GET_CURRENT_TEMPLATE_TAGS_FROM_DEFINITION_ID = "getCurrentTemplateTagsFromDefinitionId";
const GET_CURRENT_SIGNATORY_TAGS_FROM_DEFINITION_ID = "getCurrentSignatoryTagsFromDefinitionId";
const GET_CURRENT_MAPPABLE_SIGNATORY_TAGS_FROM_DEFINITION_ID = "getCurrentMappableSignatoryTagsFromDefinitionId";
const GET_CURRENT_SIGNATORY_LABEL_SET_FROM_DEFINITION_ID = "getCurrentSignatoryLabelSetFromDefinitionId";

const DOCUMENT_TEMPLATE_TYPES = {
    getters: {
        [TEMPLATE_DEFINITION_LIST]: getNamespacedResource(DOCUMENT_TEMPLATE_NAMESPACE, TEMPLATE_DEFINITION_LIST),
        [SMART_TEMPLATE_DEFINITION_LIST]: getNamespacedResource(
            DOCUMENT_TEMPLATE_NAMESPACE,
            SMART_TEMPLATE_DEFINITION_LIST
        ),
        [GET_TEMPLATE_DEFINITION_FROM_ID]: getNamespacedResource(
            DOCUMENT_TEMPLATE_NAMESPACE,
            GET_TEMPLATE_DEFINITION_FROM_ID
        ),
        [GET_CURRENT_TEMPLATE_FROM_DEFINITION_ID]: getNamespacedResource(
            DOCUMENT_TEMPLATE_NAMESPACE,
            GET_CURRENT_TEMPLATE_FROM_DEFINITION_ID
        ),
        [GET_ORDERED_MERGE_TEMPLATE_MAPPINGS_FROM_DEFINITION_ID]: getNamespacedResource(
            DOCUMENT_TEMPLATE_NAMESPACE,
            GET_ORDERED_MERGE_TEMPLATE_MAPPINGS_FROM_DEFINITION_ID
        ),
        [GET_CURRENT_TEMPLATE_TAGS_FROM_DEFINITION_ID]: getNamespacedResource(
            DOCUMENT_TEMPLATE_NAMESPACE,
            GET_CURRENT_TEMPLATE_TAGS_FROM_DEFINITION_ID
        ),
        [GET_CURRENT_SIGNATORY_TAGS_FROM_DEFINITION_ID]: getNamespacedResource(
            DOCUMENT_TEMPLATE_NAMESPACE,
            GET_CURRENT_SIGNATORY_TAGS_FROM_DEFINITION_ID
        ),
        [GET_CURRENT_MAPPABLE_SIGNATORY_TAGS_FROM_DEFINITION_ID]: getNamespacedResource(
            DOCUMENT_TEMPLATE_NAMESPACE,
            GET_CURRENT_MAPPABLE_SIGNATORY_TAGS_FROM_DEFINITION_ID
        ),
        [GET_CURRENT_SIGNATORY_LABEL_SET_FROM_DEFINITION_ID]: getNamespacedResource(
            DOCUMENT_TEMPLATE_NAMESPACE,
            GET_CURRENT_SIGNATORY_LABEL_SET_FROM_DEFINITION_ID
        )
    }
};

function templateIsUpgraded(templateDefinition) {
    return templateDefinition.UpgradeStatus === TEMPLATE_UPGRADE_STATUS.Upgraded;
}

/**
 * Create an object representing all tags associated with a document
 * template definition, including a merge template definition. For a merge
 * template definition, additionally includes a mapping from linked template
 * to the tags on the linked template. Basically a utility to ensure that
 * all getters for template tags return an object with the same shape
 * @param {*} allTags
 * @param {*} tagsByLinkTemplateDefinitionId
 * @returns
 */
function createTagsForTemplateResult(allTags, tagsByLinkTemplateDefinitionId) {
    return {
        allTags,
        tagsByLinkTemplateDefinitionId
    };
}

export { DOCUMENT_TEMPLATE_NAMESPACE, DOCUMENT_TEMPLATE_TYPES, templateIsUpgraded };

/** State should be an instance of ClientDocumentTemplateState */
export function createDocumentTemplateModule(state) {
    return {
        namespaced: true,
        state: state,
        getters: {
            [TEMPLATE_DEFINITION_LIST](state) {
                return Object.values(state.DocumentTemplateDefinitions);
            },
            [SMART_TEMPLATE_DEFINITION_LIST](state, getters) {
                return getters[TEMPLATE_DEFINITION_LIST].filter(templateIsUpgraded);
            },
            [GET_TEMPLATE_DEFINITION_FROM_ID](state) {
                return function (templateDefinitionId) {
                    return state.DocumentTemplateDefinitions[templateDefinitionId];
                };
            },
            [GET_CURRENT_TEMPLATE_FROM_DEFINITION_ID](state, getters) {
                return function (templateDefinitionId) {
                    const templateDefinition = getters[GET_TEMPLATE_DEFINITION_FROM_ID](templateDefinitionId);
                    const currentTemplateId = templateDefinition.CurrentTemplateId;
                    if (!currentTemplateId) {
                        return null;
                    }
                    return state.DocumentTemplates[currentTemplateId];
                };
            },
            [GET_ORDERED_MERGE_TEMPLATE_MAPPINGS_FROM_DEFINITION_ID](state, getters) {
                return function (templateDefinitionId) {
                    const templateDefinition = getters[GET_TEMPLATE_DEFINITION_FROM_ID](templateDefinitionId);
                    if (!templateDefinition.MergeTemplateMappings) {
                        return [];
                    }
                    // Clone to avoid this registering as a mutation
                    const mappingsClone = deepClone(templateDefinition.MergeTemplateMappings);
                    return mappingsClone.sort((a, b) => numericCompare(a.Order, b.Order));
                };
            },
            [GET_CURRENT_TEMPLATE_TAGS_FROM_DEFINITION_ID](state, getters) {
                return function (templateDefinitionId) {
                    const definition = getters[GET_TEMPLATE_DEFINITION_FROM_ID](templateDefinitionId);

                    let allTags = [];
                    const tagsByLinkTemplateDefinitionId = {};

                    if (definition.IsMergeTemplate) {
                        for (const mapping of definition.MergeTemplateMappings) {
                            if (mapping.LinkTemplateDefinitionId) {
                                const linkTemplateDefinitionId = mapping.LinkTemplateDefinitionId;
                                const childTemplateTags =
                                    getters.getCurrentTemplateTagsNonMergeTemplateFromDefinitionId(
                                        linkTemplateDefinitionId
                                    );
                                allTags = allTags.concat(childTemplateTags);
                                tagsByLinkTemplateDefinitionId[linkTemplateDefinitionId] = childTemplateTags;
                            }
                        }
                    } else {
                        allTags = getters.getCurrentTemplateTagsNonMergeTemplateFromDefinitionId(templateDefinitionId);
                    }

                    return createTagsForTemplateResult(allTags, tagsByLinkTemplateDefinitionId);
                };
            },
            [GET_CURRENT_SIGNATORY_TAGS_FROM_DEFINITION_ID](state, getters) {
                return function (templateDefinitionId) {
                    function getOnlySignatoryTags(tagList) {
                        return tagList.filter((tt) => SIGNATORY_TAG_TYPES.includes(tt.TagType));
                    }

                    const currentTags = getters[GET_CURRENT_TEMPLATE_TAGS_FROM_DEFINITION_ID](templateDefinitionId);

                    const allSignatoryTags = getOnlySignatoryTags(currentTags.allTags);
                    const signatoryTagsByLinkTemplateDefinitionId = {};

                    for (const [id, tags] of Object.entries(currentTags.tagsByLinkTemplateDefinitionId)) {
                        signatoryTagsByLinkTemplateDefinitionId[id] = getOnlySignatoryTags(tags);
                    }

                    return createTagsForTemplateResult(allSignatoryTags, signatoryTagsByLinkTemplateDefinitionId);
                };
            },
            [GET_CURRENT_MAPPABLE_SIGNATORY_TAGS_FROM_DEFINITION_ID](state, getters) {
                return function (templateDefinitionId) {
                    const signatoryTags = getters[GET_CURRENT_SIGNATORY_TAGS_FROM_DEFINITION_ID](templateDefinitionId);

                    const allMappableTags = signatoryTags.allTags.filter(canTagBeMapped);
                    const mappableTagsByLinkTemplateDefinitionId = {};

                    for (const [id, tags] of Object.entries(signatoryTags.tagsByLinkTemplateDefinitionId)) {
                        mappableTagsByLinkTemplateDefinitionId[id] = tags.filter(canTagBeMapped);
                    }

                    return createTagsForTemplateResult(allMappableTags, mappableTagsByLinkTemplateDefinitionId);
                };
            },
            [GET_CURRENT_SIGNATORY_LABEL_SET_FROM_DEFINITION_ID](state, getters) {
                return function (templateDefinitionId) {
                    const signatoryTags = getters[GET_CURRENT_SIGNATORY_TAGS_FROM_DEFINITION_ID](templateDefinitionId);
                    const labels = signatoryTags.allTags.map((tt) => tt.SignatoryLabel);
                    return new Set(labels);
                };
            },
            getCurrentTemplateTagsNonMergeTemplateFromDefinitionId(state, getters) {
                return function (templateDefinitionId) {
                    const templateDefinition = getters[GET_TEMPLATE_DEFINITION_FROM_ID](templateDefinitionId);
                    if (templateDefinitionId.IsMergeTemplate) {
                        throw `Tried to get non-merge tags for merge template ${templateDefinition.PublicId}`;
                    }

                    const currentTemplate = getters[GET_CURRENT_TEMPLATE_FROM_DEFINITION_ID](templateDefinitionId);
                    return currentTemplate.TemplateTags;
                };
            }
        }
    };
}
